import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import { useReceivables, useAbono } from "../hooks/useReceivables";
import { PriceWithDiscount } from "../../../components/PriceWithDiscount";

const COP = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
});

const METHODS = [
  { value: "CASH", label: "Efectivo" },
  { value: "TRANSFER", label: "Transferencia" },
  { value: "CARD", label: "Tarjeta" },
  { value: "OTHER", label: "Otro" },
];

function AbonoModal({ item, onHide }) {
  const abonoMutation = useAbono();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("CASH");
  const balance = Number(item?.balance_due ?? 0);

  // Pre-fill with the full outstanding balance when the modal opens (the owner
  // can lower it for a partial abono).
  useEffect(() => {
    if (item) {
      setAmount(String(item.balance_due ?? ""));
      setMethod("CASH");
    }
  }, [item]);
  const value = Number(amount);
  const invalid = !amount || value <= 0 || value > balance;

  const submit = (e) => {
    e.preventDefault();
    if (invalid) return;
    abonoMutation.mutate(
      {
        subscriptionId: item.subscription_id,
        amount: value,
        method,
        date: dayjs().format("YYYY-MM-DD"),
      },
      {
        onSuccess: (data) => {
          toast.success(
            data.is_settled
              ? "Abono registrado — paquete saldado."
              : `Abono registrado — saldo restante ${COP.format(data.balance_due)}.`
          );
          onHide();
        },
        onError: (err) =>
          toast.error(
            err?.response?.data?.amount?.[0] ??
              err?.response?.data?.detail ??
              "No se pudo registrar el abono."
          ),
      }
    );
  };

  return (
    <Modal show={Boolean(item)} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Registrar abono</Modal.Title>
      </Modal.Header>
      <Form onSubmit={submit}>
        <Modal.Body>
          <p className="text-muted small mb-3">
            <strong>{item?.student}</strong> · {item?.package}
            <br />
            Saldo pendiente: <strong>{COP.format(balance)}</strong>
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Monto del abono</Form.Label>
            <Form.Control
              type="number"
              min="0"
              max={balance}
              step="1000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
              style={{ minHeight: 44 }}
            />
            {value > balance && (
              <Form.Text className="text-danger">
                No puede superar el saldo pendiente ({COP.format(balance)}).
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label>Método</Form.Label>
            <Form.Select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              style={{ minHeight: 44 }}
            >
              {METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onHide} style={{ minHeight: 44 }}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={invalid || abonoMutation.isPending}
            style={{ minHeight: 44 }}
          >
            {abonoMutation.isPending ? <Spinner size="sm" /> : "Registrar abono"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function CarteraFinancieraPage() {
  const { data: items = [], isLoading, isError } = useReceivables();
  const [selected, setSelected] = useState(null);

  return (
    <>
      <Toaster position="top-right" />
      <div className="container-fluid px-3">
        <div className="py-3">
          <h1 className="h4 mb-0">Cartera</h1>
          <p className="text-muted small mb-0">
            Estudiantes con saldo pendiente. Registra abonos hasta saldar el
            paquete.
          </p>
        </div>

        <Card>
          <Card.Body className="p-0">
            {isLoading && (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            )}
            {isError && (
              <Alert variant="danger" className="m-3">
                No se pudo cargar la cartera.
              </Alert>
            )}
            {!isLoading && !isError && items.length === 0 && (
              <p className="text-muted text-center py-4 mb-0">
                No hay saldos pendientes. 🎉
              </p>
            )}
            {items.length > 0 && (
              <Table responsive hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Estudiante</th>
                    <th>Paquete</th>
                    <th className="text-end">Acordado</th>
                    <th className="text-end">Abonado</th>
                    <th className="text-end">Saldo</th>
                    <th>Vence</th>
                    <th className="text-end pe-3">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.subscription_id}>
                      <td className="fw-medium">
                        {it.student}
                        {it.frozen && (
                          <Badge bg="danger" className="ms-2">
                            Congelado
                          </Badge>
                        )}
                        {!it.frozen && !it.hours_delivered && (
                          <Badge bg="warning" text="dark" className="ms-2">
                            Horas retenidas
                          </Badge>
                        )}
                      </td>
                      <td>{it.package}</td>
                      <td className="text-end">
                        <PriceWithDiscount
                          listPrice={it.list_price}
                          discountPct={it.discount_pct}
                          finalPrice={it.agreed_price}
                          size="sm"
                        />
                      </td>
                      <td className="text-end">{COP.format(it.amount_paid)}</td>
                      <td className="text-end fw-semibold text-danger">
                        {COP.format(it.balance_due)}
                      </td>
                      <td>
                        {it.settle_due_date
                          ? dayjs(it.settle_due_date).format("DD/MM/YYYY")
                          : "—"}
                      </td>
                      <td className="text-end pe-3">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => setSelected(it)}
                        >
                          Abonar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </div>

      <AbonoModal item={selected} onHide={() => setSelected(null)} />
    </>
  );
}

export { CarteraFinancieraPage };
